/**
 * StatusPill primitive (Plan 02-09 / UI-SPEC §"<StatusPill kind=...>").
 *
 * Single component map of every status chip used across pool board,
 * approval queue, and my-chores. #152 legger dem på handoffens fire toner
 * (TONE_CLASS i Pill.tsx):
 *
 *   claimed   → neutral ("Tatt")
 *   awaiting  → warn ("Venter på godkjenning")
 *   approved  → ok ("Godkjent")
 *   rejected  → warn ("Avvist") — en avvist oppgave krever oppmerksomhet, og
 *               amber betyr oppmerksomhet (prinsipp 3 i design/HANDOFF.md)
 *   expired   → neutral med ink-2 ("Utløpt")
 *   sla-1     → warn ("venter i 6t")
 *   sla-2     → warn med warn-line-kant ("venter i 24t"), ett hakk sterkere
 *   age-gated → neutral; renders `{ageGate}+` (e.g. "12+") instead of
 *               an i18n label, since the number itself IS the label.
 *
 * Teksten bærer alltid betydningen; tonen er aldri eneste kjennetegn.
 *
 * All copy via i18n keys under `chores.status.*` / `approvals.sla.*` —
 * MED-7 jsx-no-literals stays green.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { TONE_CLASS } from './Pill';

export type StatusKind =
  | 'claimed'
  | 'awaiting'
  | 'approved'
  | 'rejected'
  | 'expired'
  | 'sla-1'
  | 'sla-2'
  | 'age-gated';

interface KindConfig {
  surface: string;
  tkey: string;
}

const KIND: Record<StatusKind, KindConfig> = {
  claimed: { surface: TONE_CLASS.neutral, tkey: 'chores.status.claimed' },
  awaiting: { surface: TONE_CLASS.warn, tkey: 'chores.status.awaiting' },
  approved: { surface: TONE_CLASS.ok, tkey: 'chores.status.approved' },
  rejected: { surface: TONE_CLASS.warn, tkey: 'chores.status.rejected' },
  expired: { surface: 'bg-track text-ink-2', tkey: 'chores.status.expired' },
  'sla-1': { surface: TONE_CLASS.warn, tkey: 'approvals.sla.tier1' },
  'sla-2': { surface: `${TONE_CLASS.warn} border border-warn-line`, tkey: 'approvals.sla.tier2' },
  'age-gated': { surface: TONE_CLASS.neutral, tkey: 'chores.status.ageGated' },
};

interface StatusPillProps {
  kind: StatusKind;
  /** Required only when `kind === 'age-gated'`; renders as "{ageGate}+". */
  ageGate?: number;
  className?: string;
}

export function StatusPill({ kind, ageGate, className }: StatusPillProps): ReactNode {
  const { t } = useTranslation();
  const cfg = KIND[kind];
  const label =
    kind === 'age-gated' && ageGate != null ? `${ageGate}+` : t(cfg.tkey);
  return (
    <span
      data-testid="status-pill"
      className={[
        'inline-flex h-7 items-center rounded-pill px-2.5 text-xs font-extrabold',
        cfg.surface,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {label}
    </span>
  );
}
